import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  UtensilsCrossed, Bot, Sparkles, Settings, Printer, DollarSign, ClipboardList,
  Rocket, CheckCircle2, Calendar, X, MapPin, AlertTriangle, ArrowRight,
} from "lucide-react";
import { currentRelease } from "../data/currentRelease";
import { aguardarVez, marcarAberto } from "@/utils/filaDeModais";
import { normalizarItem, separarEmojiInicial } from "@/features/novidades/texto";

/** "2026-09-01T23:59:59" -> "01/09". Data invalida devolve string vazia, e o
 *  texto simplesmente omite o prazo em vez de mostrar "Invalid Date". */
function formatarFim(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "o fim do aviso";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// Mapa key → ícone (mantém consistência com o menu lateral do Dashboard).
const ICON_BY_KEY = {
  cardapio: UtensilsCrossed,
  chatbot: Bot,
  sistema: Settings,
  impressao: Printer,
  financeiro: DollarSign,
  pedidos: ClipboardList,
  agenda: Calendar,
  atencao: AlertTriangle,
};

// Cor de cada seção: medalhão, chip e ✓. "atencao" é âmbar (aviso), o resto
// segue a cor da loja ou um tom neutro para não brigar com ela.
const COR_BY_KEY = {
  atencao: {
    medalhao: "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300",
    cartao: "border-amber-200 bg-amber-50/60 dark:border-amber-900/60 dark:bg-amber-950/20",
    chip: "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-200",
    check: "text-amber-600",
  },
  pedidos: {
    medalhao: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300",
    cartao: "border-border bg-card",
    chip: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-200",
    check: "text-emerald-600",
  },
};
const COR_PADRAO = {
  medalhao: "bg-primary/10 text-primary",
  cartao: "border-border bg-card",
  chip: "bg-primary/10 text-primary",
  check: "text-primary",
};

// Pontinhos de brilho do cabeçalho: posição e atraso fixos (nada aleatório,
// para a tela não mudar a cada abertura).
const BRILHOS = [
  { top: "14%", left: "58%", delay: "0ms", size: "text-base" },
  { top: "32%", left: "84%", delay: "400ms", size: "text-xs" },
  { top: "62%", left: "70%", delay: "800ms", size: "text-sm" },
  { top: "20%", left: "92%", delay: "1200ms", size: "text-xs" },
  { top: "70%", left: "90%", delay: "600ms", size: "text-base" },
];

/**
 * Popup de release note. Renderizado no Dashboard.
 *
 * Regras de exibição:
 *  - Só aparece se `now` está dentro de `[starts_at, expires_at]`.
 *  - sessionStorage "release_seen_<id>" evita reabrir no mesmo tab após ver.
 *  - Fechar/reabrir o browser (nova sessão) reexibe.
 *  - "Não ver esta notificação novamente" marcada grava localStorage
 *    "release_dismissed_<id>": aquele release nunca mais abre naquele
 *    navegador. Como o `id` muda a cada release, o próximo aparece normal.
 *
 * Visual (02/11/2026, pedido do dono): uma aba por seção (Novidades,
 * Melhorias, Correções, Fique atento), cada novidade num cartão com medalhão
 * (emoji do título), descrição curta e o chip "Onde fica" (📍 da descrição).
 * Os cartões entram em cascata; o botão do rodapé leva à próxima aba e, na
 * última, fecha. Tudo com utilitários do Tailwind (tailwindcss-animate), sem
 * biblioteca nova; `motion-reduce` desliga as animações.
 */
const chaveDispensado = (id) => `release_dismissed_${id}`;

export default function ReleaseNotesModal() {
  const [open, setOpen] = useState(false);
  const [naoMostrar, setNaoMostrar] = useState(false);
  const secoes = useMemo(
    () => (currentRelease?.sections || []).map((s) => ({
      ...s,
      rotulo: separarEmojiInicial(s.title),
      itens: (s.items || []).map(normalizarItem),
    })),
    [],
  );
  const [aba, setAba] = useState(secoes[0]?.key || "");

  useEffect(() => {
    if (!currentRelease?.id) return;
    // Fora da janela?
    const now = new Date();
    const starts = new Date(currentRelease.starts_at);
    const expires = new Date(currentRelease.expires_at);
    if (isNaN(starts.getTime()) || isNaN(expires.getTime())) return;
    if (now < starts || now > expires) return;
    // Já visto nesta sessão?
    const seenKey = `release_seen_${currentRelease.id}`;
    if (sessionStorage.getItem(seenKey)) return;
    // Dispensado de vez neste navegador ("Não ver esta notificação novamente")?
    try {
      if (localStorage.getItem(chaveDispensado(currentRelease.id))) return;
    } catch { /* localStorage indisponível — segue como não dispensado */ }
    // Espera a vez na fila (utils/filaDeModais): abre só depois que o aviso de
    // loja fechada sair. Antes esta checagem era feita aqui na mão, olhando
    // direto o `storePromptOpen`; agora a ordem de prioridade dos três modais
    // do boot vive num lugar só.
    return aguardarVez("novidades", () => setOpen(true));
  }, []);

  // Enquanto aberto, sinaliza pra quem vem depois (o tour) esperar.
  useEffect(() => {
    if (!open) return undefined;
    return marcarAberto("novidades");
  }, [open]);

  // Toda saída passa aqui (X, Confirmar, clique fora, Esc). A caixa marcada
  // vale em qualquer uma delas: quem marcou não quer ver de novo.
  const handleClose = () => {
    setOpen(false);
    if (currentRelease?.id) {
      try {
        sessionStorage.setItem(`release_seen_${currentRelease.id}`, "1");
        if (naoMostrar) localStorage.setItem(chaveDispensado(currentRelease.id), "1");
      } catch { /* storage indisponível — segue */ }
    }
  };

  if (!currentRelease?.id) return null;

  const indice = Math.max(0, secoes.findIndex((s) => s.key === aba));
  const proxima = secoes[indice + 1];
  const ultimaAba = !proxima;

  return (
    <Dialog open={open} onOpenChange={(aberto) => { if (!aberto) handleClose(); }}>
      <DialogContent
        // hideClose: o X padrão do DialogContent ficava empilhado com o X
        // próprio do cabeçalho (dois botões no mesmo canto). Fica só o nosso.
        hideClose
        // Sem foco automático: o Radix focava o X ao abrir e o contorno de
        // foco desenhava um segundo anel em volta dele. Tab continua preso
        // no popup e Esc continua fechando.
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="sm:max-w-2xl max-h-[92vh] p-0 overflow-hidden flex flex-col"
      >
        {/* ===== HEADER — gradient primary + brilhos + versão + botão fechar ===== */}
        <DialogHeader className="p-0 space-y-0">
          <div className="relative overflow-hidden bg-gradient-to-br from-primary via-primary to-primary/80 px-6 pt-6 pb-12 text-primary-foreground">
            {/* Faixa de luz que atravessa o cabeçalho uma vez */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-white/25 to-transparent animate-shimmer motion-reduce:hidden"
              style={{ animationIterationCount: 1, animationDuration: "1.6s" }}
            />
            {BRILHOS.map((b, i) => (
              <span
                key={i}
                aria-hidden
                className={`pointer-events-none absolute select-none opacity-80 animate-pulse motion-reduce:animate-none ${b.size}`}
                style={{ top: b.top, left: b.left, animationDelay: b.delay }}
              >
                ✦
              </span>
            ))}
            {/* Botão X — o único do popup (ver hideClose acima) */}
            <button
              type="button"
              onClick={handleClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur flex items-center justify-center transition-colors"
              aria-label="Fechar aviso"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center animate-in zoom-in-50 duration-500 motion-reduce:animate-none">
                <Rocket className="w-5 h-5" />
              </div>
              <span className="text-[11px] uppercase tracking-wider font-bold opacity-90 rounded-full bg-white/15 px-2.5 py-1">
                {currentRelease.version} · {currentRelease.date}
              </span>
            </div>
            <DialogTitle asChild>
              <h2 className="text-2xl font-black leading-tight pr-10 animate-in fade-in slide-in-from-bottom-2 duration-500 motion-reduce:animate-none">
                {currentRelease.title}
              </h2>
            </DialogTitle>
            <p className="text-sm text-primary-foreground/85 mt-1.5">
              Toque numa aba para ver cada parte. Cada novidade diz onde fica no menu.
            </p>
          </div>
          {/* "Onda" branca separando o header do conteúdo */}
          <div className="-mt-5 h-5 bg-background rounded-t-3xl" />
        </DialogHeader>

        {/* ===== ABAS ===== */}
        <Tabs value={aba} onValueChange={setAba} className="flex-1 min-h-0 flex flex-col">
          <div className="px-4 sm:px-6">
            <TabsList className="grid w-full h-auto grid-cols-2 sm:grid-cols-4 gap-1 rounded-xl p-1">
              {secoes.map((s) => (
                <TabsTrigger
                  key={s.key}
                  value={s.key}
                  className="h-auto flex items-center justify-center gap-1.5 rounded-lg px-1.5 py-2 text-xs sm:text-[13px] whitespace-nowrap data-[state=active]:shadow-sm"
                >
                  <span aria-hidden className="text-base leading-none">{s.rotulo.emoji}</span>
                  <span className="font-semibold">{s.rotulo.texto}</span>
                  <span className="ml-0.5 inline-flex min-w-[1.25rem] justify-center rounded-full bg-muted-foreground/15 px-1.5 text-[10px] font-bold tabular-nums">
                    {s.itens.length}
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {/* ===== CORPO SCROLLÁVEL ===== */}
          <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 pt-3 pb-4">
            {secoes.map((s) => {
              const Icon = ICON_BY_KEY[s.key] || Sparkles;
              const cor = COR_BY_KEY[s.key] || COR_PADRAO;
              return (
                <TabsContent key={s.key} value={s.key} className="mt-0 focus-visible:ring-0">
                  <ul className="space-y-2.5">
                    {s.itens.map((it, i) => {
                      // Cascata: cada cartão entra um pouco depois do anterior
                      // (limite para a lista comprida não demorar a aparecer).
                      const entrada = {
                        animationDelay: `${Math.min(i, 8) * 55}ms`,
                        animationDuration: "380ms",
                      };
                      if (it.simples) {
                        return (
                          <li
                            key={i}
                            style={entrada}
                            className="flex items-start gap-2.5 rounded-lg px-2 py-1.5 text-sm leading-relaxed animate-in fade-in slide-in-from-bottom-2 fill-mode-both motion-reduce:animate-none"
                          >
                            <CheckCircle2 className={`w-4 h-4 flex-shrink-0 mt-0.5 ${cor.check}`} />
                            <span>{it.corpo}</span>
                          </li>
                        );
                      }
                      return (
                        <li
                          key={i}
                          style={entrada}
                          className={`flex items-start gap-3 rounded-xl border p-3 shadow-sm animate-in fade-in slide-in-from-bottom-2 fill-mode-both motion-reduce:animate-none ${cor.cartao}`}
                        >
                          <div className={`w-11 h-11 flex-shrink-0 rounded-xl flex items-center justify-center text-2xl leading-none ${cor.medalhao}`}>
                            {it.emoji ? <span aria-hidden>{it.emoji}</span> : <Icon className="w-5 h-5" />}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-bold leading-tight">{it.titulo}</p>
                            {it.corpo && (
                              <p className="mt-0.5 text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{it.corpo}</p>
                            )}
                            {it.caminho && (
                              <span className={`mt-2 inline-flex max-w-full items-start gap-1 rounded-lg px-2.5 py-1 text-xs font-medium leading-snug ${cor.chip}`}>
                                <MapPin className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                <span className="break-words">Onde fica: {it.caminho}</span>
                              </span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>

                  {/* ===== FUTURAS ATUALIZAÇÕES (só na primeira aba) ===== */}
                  {s.key === secoes[0]?.key && (currentRelease.coming_soon || []).length > 0 && (
                    <section className="mt-4 rounded-xl border-2 border-dashed border-primary/40 bg-primary/[0.04] p-4 animate-in fade-in fill-mode-both motion-reduce:animate-none" style={{ animationDelay: "500ms" }}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shadow">
                          <Sparkles className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="font-black text-sm uppercase tracking-wider">Futuras atualizações</h3>
                          <p className="text-[11px] text-muted-foreground">Tá vindo aí, aguarde 👀</p>
                        </div>
                      </div>
                      <ul className="space-y-1.5 pl-1">
                        {currentRelease.coming_soon.map((cs, i) => (
                          <li key={i} className="flex items-start gap-2.5">
                            <span className="text-primary text-xs mt-1">▸</span>
                            <div className="min-w-0">
                              <p className="font-bold text-sm leading-tight">{cs.title}</p>
                              {cs.desc && <p className="text-xs text-muted-foreground mt-0.5">{cs.desc}</p>}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}
                </TabsContent>
              );
            })}
          </div>
        </Tabs>

        {/* ===== FOOTER ===== */}
        <div className="border-t bg-muted/30 px-4 sm:px-6 py-3 flex-shrink-0">
          {ultimaAba ? (
            <Button
              onClick={handleClose}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold gap-2"
            >
              <CheckCircle2 className="w-4 h-4" />
              Entendi, vamos lá!
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleClose} className="flex-shrink-0 font-semibold">
                Fechar
              </Button>
              <Button
                onClick={() => setAba(proxima.key)}
                className="flex-1 min-w-0 bg-primary hover:bg-primary/90 text-primary-foreground font-bold gap-2"
              >
                <span className="truncate">Ver {proxima.rotulo.emoji} {proxima.rotulo.texto}</span>
                <ArrowRight className="w-4 h-4 flex-shrink-0" />
              </Button>
            </div>
          )}
          <div className="mt-2.5 flex items-center justify-center gap-1.5" aria-hidden>
            {secoes.map((s, i) => (
              <span
                key={s.key}
                className={`h-1.5 rounded-full transition-all ${i === indice ? "w-5 bg-primary" : "w-1.5 bg-muted-foreground/30"}`}
              />
            ))}
          </div>
          <label
            htmlFor="release-nao-mostrar"
            className="mt-2 flex items-center justify-center gap-2 text-xs text-muted-foreground cursor-pointer select-none"
          >
            <Checkbox
              id="release-nao-mostrar"
              checked={naoMostrar}
              onCheckedChange={(v) => setNaoMostrar(v === true)}
            />
            Não ver esta notificação novamente
          </label>
          <p className="text-[10px] text-muted-foreground text-center mt-1.5">
            {/* Sai do proprio `expires_at` em vez de "2 dias" fixo: a janela ja'
                mudou de tamanho entre releases e o texto ficou mentindo. */}
            Este aviso fica visível até {formatarFim(currentRelease.expires_at)}.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
